// AI レポートの KPI ツリーの定義(設計書 2026-10-13 §3.2)を管理画面で組み立てるための操作。
// どれも元の定義を書き換えず、新しい定義を返す。検証はサーバー(TreeSpec.validate)だけで行う
// 保存済みの定義が無効(段0個・nodes が無い・conversions が配列でない等)でも例外を出さずに操作できるようにする

const clone = (spec) => structuredClone(spec)
const conversionsOf = (spec) => (Array.isArray(spec.conversions) ? spec.conversions : [])
const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value)
// nodes が配列でない段(無効な保存済みの定義)は空の配列から組み立て直す
const nodesOf = (stage) => {
  if (!Array.isArray(stage.nodes)) stage.nodes = []
  return stage.nodes
}

// 段・ノードがオブジェクトでない定義は編集部品で描けないので null(JSON の欄をそのまま見せて直してもらう)
export function parseSpec(text) {
  try {
    const spec = JSON.parse(text)
    if (!isObject(spec) || !Array.isArray(spec.stages)) return null
    const drawable = spec.stages.every((s) => isObject(s) && (!Array.isArray(s.nodes) || s.nodes.every(isObject)))
    return drawable ? spec : null
  } catch {
    return null
  }
}

export function nextStageKey(spec) {
  const keys = new Set(spec.stages.map((s) => s.key))
  let n = spec.stages.length + 1
  while (keys.has(`stage${n}`)) n += 1
  return `stage${n}`
}

export function addStage(spec) {
  const out = clone(spec)
  out.stages.push({ key: nextStageKey(spec), title: "新しい段", nodes: [] })
  return out
}

export function removeStage(spec, index) {
  const out = clone(spec)
  const [removed] = out.stages.splice(index, 1)
  out.conversions = conversionsOf(out).filter((c) => c?.after !== removed?.key)
  return out
}

function move(list, index, dir) {
  const to = index + dir
  if (to < 0 || to >= list.length) return false
  ;[list[index], list[to]] = [list[to], list[index]]
  return true
}

export function moveStage(spec, index, dir) {
  const out = clone(spec)
  return move(out.stages, index, dir) ? out : spec
}

export function updateStage(spec, index, patch) {
  const out = clone(spec)
  const stage = Object.assign(out.stages[index], patch)
  if (stage.ai_title === "") delete stage.ai_title
  if (stage.ai === true) delete stage.ai
  return out
}

export function addNode(spec, stageIndex, metric) {
  const out = clone(spec)
  nodesOf(out.stages[stageIndex]).push({ metric })
  return out
}

export function addSeparator(spec, stageIndex) {
  const out = clone(spec)
  nodesOf(out.stages[stageIndex]).push({ separator: true })
  return out
}

export function removeNode(spec, stageIndex, nodeIndex) {
  const out = clone(spec)
  nodesOf(out.stages[stageIndex]).splice(nodeIndex, 1)
  return out
}

export function moveNode(spec, stageIndex, nodeIndex, dir) {
  const out = clone(spec)
  return move(nodesOf(out.stages[stageIndex]), nodeIndex, dir) ? out : spec
}

export function updateNode(spec, stageIndex, nodeIndex, patch) {
  const out = clone(spec)
  const node = Object.assign(nodesOf(out.stages[stageIndex])[nodeIndex], patch)
  if (node.child === false) delete node.child
  return out
}

export function conversionAfter(spec, stageKey) {
  return conversionsOf(spec).find((c) => c?.after === stageKey)?.rate || ""
}

// 段 stageKey の後ろの率を rate にする(空なら消す)。並びは段の順
export function setConversion(spec, stageKey, rate) {
  const out = clone(spec)
  const rest = conversionsOf(out).filter((c) => c?.after !== stageKey)
  if (rate) rest.push({ after: stageKey, rate })
  const order = new Map(out.stages.map((s, i) => [s.key, i]))
  out.conversions = rest.sort((a, b) => (order.get(a?.after) ?? 99) - (order.get(b?.after) ?? 99))
  return out
}

// 段の削除・並べ替えのあと、存在しない段・最後の段の後ろの率を消す
export function pruneConversions(spec) {
  const keys = spec.stages.map((s) => s.key)
  const last = keys[keys.length - 1]
  const out = clone(spec)
  out.conversions = conversionsOf(out).filter((c) => keys.includes(c?.after) && c.after !== last)
  return out
}

export function errorsAt(errors, path) {
  const want = JSON.stringify(path)
  return (errors || []).filter((e) => JSON.stringify(e.path) === want).map((e) => e.message)
}
